import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Calendar, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface FSAConfigFormProps {
  planId: number;
  fsaType: "health_care" | "dependent_care";
  onSave?: () => void;
}

// Blank by default — empty inputs (with placeholders) make it obvious nothing is
// saved yet. Real values populate only when a saved config loads.
const EMPTY_FORM = {
  fsaType: "",
  annualElectionMin: "",
  annualElectionMax: "",
  employerContribution: "",
  gracePeriodDays: "",
  runOutPeriodDays: "",
  carryoverAllowed: false,
  carryoverMax: "",
  prorationMethod: "none" as "none" | "monthly" | "daily",
  midYearChangeAllowed: false,
  claimFilingDeadlineDays: "",
};

export function FSAConfigForm({ planId, fsaType, onSave }: FSAConfigFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the config fetch has resolved, and whether a saved row exists.
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configExists, setConfigExists] = useState(false);
  // JSON snapshot of the last saved/loaded form, to detect unsaved edits.
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");

  const [formData, setFormData] = useState({ ...EMPTY_FORM, fsaType });

  const isDirty = configLoaded && JSON.stringify(formData) !== savedSnapshot;

  // Load existing config
  useEffect(() => {
    fetch(`/api/plans/${planId}/fsa-config`, { credentials: "include" })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          const loaded = {
            fsaType: data.fsaType || fsaType,
            annualElectionMin: data.annualElectionMin || "",
            annualElectionMax: data.annualElectionMax || "",
            employerContribution: data.employerContribution || "",
            gracePeriodDays: data.gracePeriodDays != null ? String(data.gracePeriodDays) : "",
            runOutPeriodDays: data.runOutPeriodDays != null ? String(data.runOutPeriodDays) : "",
            carryoverAllowed: data.carryoverAllowed || false,
            carryoverMax: data.carryoverMax || "",
            prorationMethod: data.prorationMethod || "none",
            midYearChangeAllowed: data.midYearChangeAllowed || false,
            claimFilingDeadlineDays: data.claimFilingDeadlineDays != null ? String(data.claimFilingDeadlineDays) : "",
          };
          setFormData(loaded as typeof formData);
          setSavedSnapshot(JSON.stringify(loaded));
          setConfigExists(true);
        } else {
          const empty = { ...EMPTY_FORM, fsaType };
          setSavedSnapshot(JSON.stringify(empty));
          setConfigExists(false);
        }
        setConfigLoaded(true);
      })
      .catch(() => setConfigLoaded(true));
  }, [planId, fsaType]);

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/plans/${planId}/fsa-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          planId,
          ...formData,
          gracePeriodDays: Number(formData.gracePeriodDays) || 0,
          runOutPeriodDays: Number(formData.runOutPeriodDays) || 0,
          claimFilingDeadlineDays: Number(formData.claimFilingDeadlineDays) || 0,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to save");
      }

      toast({ title: "Saved", description: "FSA configuration updated." });
      // Refresh every surface that reads FSA config (plan banner, readiness
      // badges, enrollment pickers) so warnings clear without a manual refresh.
      queryClient.invalidateQueries({
        predicate: (q) => JSON.stringify(q.queryKey).includes("fsa-config"),
      });
      setConfigExists(true);
      setSavedSnapshot(JSON.stringify(formData));
      onSave?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {configLoaded && !configExists && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 flex items-start gap-2 text-sm text-amber-900 dark:text-amber-100">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            This plan has no saved FSA configuration yet. The fields below are
            blank — suggested IRS limits appear only as placeholders. Nothing is
            saved until you click <b>Save FSA Configuration</b>.
          </span>
        </div>
      )}
      {/* Annual Election Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Annual Election Limits
          </CardTitle>
          <CardDescription>
            Set minimum and maximum annual election amounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="annualElectionMin">Minimum Election ($)</Label>
              <Input
                id="annualElectionMin"
                type="number"
                value={formData.annualElectionMin}
                onChange={(e) => updateField("annualElectionMin", e.target.value)}
                placeholder="100"
              />
            </div>
            <div>
              <Label htmlFor="annualElectionMax">Maximum Election ($)</Label>
              <Input
                id="annualElectionMax"
                type="number"
                value={formData.annualElectionMax}
                onChange={(e) => updateField("annualElectionMax", e.target.value)}
                placeholder={fsaType === "health_care" ? "3200" : "5000"}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {fsaType === "health_care"
                  ? "IRS limit for 2024: $3,200"
                  : "IRS limit for 2024: $5,000 ($2,500 if married filing separately)"}
              </p>
            </div>
          </div>

          <Separator />

          <div>
            <Label htmlFor="employerContribution">Employer Contribution ($ per year)</Label>
            <Input
              id="employerContribution"
              type="number"
              value={formData.employerContribution}
              onChange={(e) => updateField("employerContribution", e.target.value)}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Fixed annual amount the employer contributes. Combined with the employee's election it cannot exceed the maximum above.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Grace Period & Carryover */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Plan Rules
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gracePeriodDays">Grace Period (days)</Label>
              <Input
                id="gracePeriodDays"
                type="number"
                value={formData.gracePeriodDays}
                onChange={(e) => updateField("gracePeriodDays", e.target.value)}
                placeholder="75"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Standard: 75 days for HCFSA, typically 0 for DCFSA
              </p>
            </div>
            <div>
              <Label htmlFor="runOutPeriodDays">Run-Out Period (days)</Label>
              <Input
                id="runOutPeriodDays"
                type="number"
                value={formData.runOutPeriodDays}
                onChange={(e) => updateField("runOutPeriodDays", e.target.value)}
                placeholder="90"
              />
            </div>
          </div>

          <Separator />

          <div className="flex items-center space-x-2">
            <Checkbox
              id="carryoverAllowed"
              checked={formData.carryoverAllowed}
              onCheckedChange={(checked) => updateField("carryoverAllowed", checked)}
            />
            <Label htmlFor="carryoverAllowed" className="cursor-pointer">
              Allow Carryover (unused funds carry to next year)
            </Label>
          </div>

          {formData.carryoverAllowed && (
            <div>
              <Label htmlFor="carryoverMax">Maximum Carryover ($)</Label>
              <Input
                id="carryoverMax"
                type="number"
                value={formData.carryoverMax}
                onChange={(e) => updateField("carryoverMax", e.target.value)}
                placeholder="640"
              />
              <p className="text-xs text-muted-foreground mt-1">
                IRS limit for 2024: $640
              </p>
            </div>
          )}

          <Separator />

          <div>
            <Label>Proration Method</Label>
            <Select value={formData.prorationMethod} onValueChange={(v) => updateField("prorationMethod", v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Proration</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="midYearChangeAllowed"
              checked={formData.midYearChangeAllowed}
              onCheckedChange={(checked) => updateField("midYearChangeAllowed", checked)}
            />
            <Label htmlFor="midYearChangeAllowed" className="cursor-pointer">
              Allow Mid-Year Election Changes
            </Label>
          </div>

          <div>
            <Label htmlFor="claimFilingDeadlineDays">Claim Filing Deadline (days after plan year)</Label>
            <Input
              id="claimFilingDeadlineDays"
              type="number"
              value={formData.claimFilingDeadlineDays}
              onChange={(e) => updateField("claimFilingDeadlineDays", e.target.value)}
              placeholder="90"
            />
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {isDirty ? (
          <span className="text-xs text-amber-700 dark:text-amber-300">Unsaved changes</span>
        ) : configExists ? (
          <span className="text-xs text-stone-500">All changes saved</span>
        ) : null}
        <Button type="submit" disabled={loading || !isDirty}>
          {loading ? "Saving..." : "Save FSA Configuration"}
        </Button>
      </div>
    </form>
  );
}
